import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ImportValidationError, type ImportResult } from '../index.js'
import type { ClientsAdminApi } from '../adminApi.js'
import { type AdminFacility } from '../../facilities/index.js'
import { previewCsv, previewJsonArray, rosterFacilityIds, type ImportPreview } from './importPreview.js'
import { ImportPreviewModal } from './ImportPreviewModal.js'
import { Eye } from 'lucide-react'
import { Button } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = ClientsAdminApi

const MAX_ERRORS_SHOWN = 5

interface ImportRowProps {
  label: string
  hint: string
  accept: string
  noun: [singular: string, plural: string]
  inputId: string
  toPreview: (content: string) => ImportPreview
  /** Returns a reason not to send this file at all, or null to go ahead. */
  precheck?: (preview: ImportPreview) => string | null
  runImport: (content: string) => Promise<ImportResult>
  onImported: () => Promise<void>
}

interface OpenPreview {
  fileName: string
  content: string
  preview: ImportPreview
}

/** One file type: choose a file, preview it in a modal, import from there, and see exactly what happened to it. */
function ImportRow({ label, hint, accept, noun, inputId, toPreview, precheck, runImport, onImported }: ImportRowProps) {
  const [file, setFile] = useState<File | null>(null)
  const [open, setOpen] = useState<OpenPreview | null>(null)

  const mutation = useMutation({
    mutationFn: (content: string) => runImport(content),
    onSuccess: () => onImported(),
    // Success or failure, the outcome is reported on the row the file was chosen in.
    onSettled: () => setOpen(null),
  })

  async function openPreview(chosen: File): Promise<void> {
    const content = await chosen.text()
    mutation.reset()
    setOpen({ fileName: chosen.name, content, preview: toPreview(content) })
  }

  const result = mutation.data ?? null
  const rowErrors = mutation.error instanceof ImportValidationError ? mutation.error.rows : []

  return (
    <div className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor={inputId} className="text-xs font-bold text-ink w-24">
          {label}
        </label>
        <input
          id={inputId}
          type="file"
          accept={accept}
          className="text-xs text-ink-mid file:mr-3 file:border file:border-marine file:bg-white file:px-3 file:py-1 file:text-xs file:font-bold file:text-marine"
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null)
            mutation.reset()
          }}
        />
        <Button icon={Eye} size="sm" disabled={!file} onClick={() => file && void openPreview(file)}>
          Preview…
        </Button>
      </div>
      <p className="text-[11px] text-ink-mid mt-1">{hint}</p>

      {result && (
        <p className={`text-[11px] font-bold mt-2 ${result.errors.length > 0 ? 'text-coral' : 'text-green'}`}>
          Imported {result.imported.toLocaleString()} {result.imported === 1 ? noun[0] : noun[1]}
          {result.errors.length > 0 && ` - ${result.errors.length} could not be saved: ${result.errors[0]?.error}`}
        </p>
      )}

      {mutation.isError && (
        <div className="text-[11px] text-coral mt-2">
          <p className="font-bold">{mutation.error.message}</p>
          {rowErrors.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {rowErrors.slice(0, MAX_ERRORS_SHOWN).map((rowError) => (
                <li key={`${rowError.row}-${rowError.error}`}>
                  {rowError.row > 0 ? `Row ${rowError.row}: ` : ''}
                  {rowError.error}
                </li>
              ))}
              {rowErrors.length > MAX_ERRORS_SHOWN && <li>…and {rowErrors.length - MAX_ERRORS_SHOWN} more.</li>}
            </ul>
          )}
        </div>
      )}

      {open && (
        <ImportPreviewModal
          title={`Import ${label.toLowerCase()}`}
          fileName={open.fileName}
          preview={open.preview}
          noun={noun}
          blocked={precheck?.(open.preview) ?? null}
          isImporting={mutation.isPending}
          onConfirm={() => mutation.mutate(open.content)}
          onCancel={() => setOpen(null)}
        />
      )}
    </div>
  )
}

/**
 * Bulk-loads a client's facilities and workers from files, through the same
 * import endpoints the onboarding runbook uses. Nothing is sent until the
 * file has been previewed. Both imports upsert, so a file can be imported
 * again to correct it. Workers are checked against the client's registered
 * facilities first: the API would accept a roster for a facility that
 * doesn't exist, and those workers would then appear nowhere.
 */
export function ClientDataImport({ api, clientId, facilities }: { api: AdminApi; clientId: string; facilities: AdminFacility[] | undefined }) {
  const queryClient = useQueryClient()

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['admin'] })
  }

  function checkRosterFacilities(preview: ImportPreview): string | null {
    const referenced = rosterFacilityIds(preview)
    if (!referenced || !facilities) {
      return null
    }
    const known = new Set(facilities.map((facility) => facility.facilityId))
    const unknown = referenced.filter((facilityId) => !known.has(facilityId))
    return unknown.length > 0
      ? `This file refers to ${unknown.length === 1 ? 'a facility' : 'facilities'} this client doesn't have (${unknown.join(', ')}). Import the facilities first.`
      : null
  }

  return (
    <div className="border border-marine/15 bg-white divide-y divide-marine/10">
      <ImportRow
        label="Facilities"
        hint='A .json file: an array of { "facilityId", "name" }. Importing a facilityId that already exists renames it.'
        accept=".json,application/json"
        noun={['facility', 'facilities']}
        inputId={`import-facilities-${clientId}`}
        toPreview={previewJsonArray}
        runImport={(content) => api.importFacilities(clientId, content)}
        onImported={refresh}
      />
      <ImportRow
        label="Workers"
        hint="A roster .csv with a facilityId on every row. Import facilities first. Existing workerIds are updated, never duplicated."
        accept=".csv,text/csv"
        noun={['worker', 'workers']}
        inputId={`import-workers-${clientId}`}
        toPreview={previewCsv}
        precheck={checkRosterFacilities}
        runImport={(content) => api.importWorkers(clientId, content)}
        onImported={refresh}
      />
    </div>
  )
}
